/** Estado de autenticação compartilhado pela aplicação. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { Api } from '../api/endpoints';
import { armazenamento, registrarPerdaDeSessao, revogarSessao, sessaoRecusada } from '../api/client';
import type { UsuarioAutenticado } from '../types';

interface EstadoAuth {
  usuario: UsuarioAutenticado | null;
  carregando: boolean;
  entrar: (loja: string, usuario: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Relê `/auth/me`. Usada quando a marca da loja muda: nome e brasão vêm
   *  dentro do usuário, e sem reler a barra lateral ficaria com o antigo. */
  recarregar: () => Promise<void>;
  /** True se o usuário tem a permissão no formato `modulo:acao`. */
  pode: (permissao: string) => boolean;
  /** Maior grau que o usuário pode ler (1–3). Sem vínculo com o quadro, 3. */
  grauMaximo: number;
}

const Contexto = createContext<EstadoAuth | null>(null);

function usuarioSalvo(): UsuarioAutenticado | null {
  const bruto = armazenamento.usuario;
  if (!bruto) return null;
  try {
    return JSON.parse(bruto) as UsuarioAutenticado;
  } catch {
    return null;
  }
}

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioAutenticado | null>(usuarioSalvo);
  // Com o usuário já salvo, a interface abre na hora e a sessão é revalidada
  // em segundo plano. Antes a tela inteira esperava o `/auth/me` — uma ida e
  // volta completa (≈0,5 s em produção, 1,5 s no local) antes de a página
  // sequer começar a pedir os próprios dados. Token vencido não é problema: o
  // interceptor do cliente renova e repete, e sessão perdida cai no login.
  const [carregando, setCarregando] = useState(
    () => Boolean(armazenamento.access) && usuarioSalvo() === null,
  );

  // Revalida a sessão restaurada: permissões podem ter mudado e o token pode
  // ter expirado enquanto a aba estava fechada.
  useEffect(() => {
    if (!armazenamento.access) {
      setCarregando(false);
      return;
    }
    let ativo = true;
    Api.eu()
      .then((atual) => {
        if (!ativo) return;
        setUsuario(atual);
        armazenamento.salvarUsuario(atual);
      })
      .catch((falha) => {
        if (!ativo) return;
        // API fora do ar não é sessão perdida: segue com o usuário guardado e a
        // próxima chamada revalida. Só cai no login quando o servidor recusa.
        if (!sessaoRecusada(falha)) return;
        armazenamento.limpar();
        setUsuario(null);
      })
      .finally(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    registrarPerdaDeSessao(() => setUsuario(null));
  }, []);

  // Sair numa aba tem de sair em todas, e entrar numa tem de valer nas outras:
  // com o token no `localStorage` elas dividem a sessão, e deixar uma aba
  // mostrando a interface de quem já saiu seria pior do que não sincronizar.
  useEffect(
    () =>
      armazenamento.aoMudarEmOutraAba(() => {
        if (!armazenamento.access) {
          setUsuario(null);
          return;
        }
        const salvo = usuarioSalvo();
        if (salvo) setUsuario(salvo);
      }),
    [],
  );

  const entrar = useCallback(async (loja: string, login: string, senha: string) => {
    const resposta = await Api.login(loja, login, senha);
    armazenamento.salvarTokens(resposta.access_token, resposta.refresh_token);
    armazenamento.salvarUsuario(resposta.usuario);
    setUsuario(resposta.usuario);
  }, []);

  const recarregar = useCallback(async () => {
    const atual = await Api.eu();
    setUsuario(atual);
    armazenamento.salvarUsuario(atual);
  }, []);

  // A tela sai na hora; revogar no servidor fica em segundo plano. Esperar a
  // resposta deixava o botão parado por segundos — mais ainda com o access
  // vencido, que custava renovar antes de conseguir sair.
  const sair = useCallback(async () => {
    const { access, refresh } = armazenamento;
    armazenamento.limpar();
    setUsuario(null);
    revogarSessao(access, refresh).catch(() => {
      // Revogar no servidor é o ideal, mas a sessão local já saiu.
    });
  }, []);

  const valor = useMemo<EstadoAuth>(
    () => ({
      usuario,
      carregando,
      entrar,
      sair,
      recarregar,
      pode: (permissao) => usuario?.permissoes.includes(permissao) ?? false,
      // Conta de sistema lê tudo; irmão ainda sem grau registrado lê como Aprendiz (como o servidor).
      grauMaximo: usuario?.membro_id == null ? 3 : Math.min(usuario.grau_nivel ?? 1, 3),
    }),
    [usuario, carregando, entrar, sair, recarregar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): EstadoAuth {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAuth precisa estar dentro de <ProvedorAuth>');
  return contexto;
}
